import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { LocalStorageService } from '@ndk/ng-storage';
import { forkJoin as observableForkJoin, Observable, of as observableOf } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';

import { AuthTokenService } from '@core/authentication/auth-token.service';
import { AccountTypes } from '@core/authentication/shared/account-types.enum';
import { LocalStorageKeys } from '@core/storage/local-storage.enum';
import { QuotaBehaviorTypes } from '@features/quota/shared/quota-behavior-types.enum';
import { QuotaObjectTypes } from '@features/quota/shared/quota-object-types.enum';
import { SettingsQuotasActivationBehaviorService } from '@features/settings/quotas/activation-behavior/shared/settings-quotas-activation-behavior.service';
import { SettingsQuotasTypeService } from '@features/settings/quotas/type/shared/settings-quotas-type.service';
import { ManagerPolicyPermissionTypes } from '@features/settings/users/managers/shared/settings-manager-permission-policies.enum';
import { ManagerQuotaPermissionTypes } from '@features/settings/users/managers/shared/settings-manager-permission-quotas.enum';
import { IManagerPolicyPermission, IManagerQuotaPermission } from '@features/settings/users/managers/shared/settings-manager-permissions.model';
import { SettingsManagerPermissionsService } from '@features/settings/users/managers/shared/settings-manager-permissions.service';
import { IUserManagerPermissionPolicies } from '@features/user/shared/user-manager-permission-policies.model';
import { IUserManagerPermissionQuotas } from '@features/user/shared/user-manager-permission-quotas.model';
import { UserService } from '@features/user/shared/user.service';

/** Tipo de retorno para o Subject `refreshHasPoliciesQuotasManagement$`. */
export type PoliciesQuotasManagement = { policies: ManagerPolicyPermissionTypes, quotas: ManagerQuotaPermissionTypes };

/** Guard para validar se há admnistração de políticas e/ou cotas habilitada(s). */
@Injectable()
export class NotAllowDisabledManagementPoliciesQuotasGuardService implements CanActivate {
    /** Nome da propriedade para não validar administração de políticas (o valor é apenas salvo em Local Storage). */
    private static DONT_VALIDATE_POLICIES_PROPERTY_NAME: string = 'dontValidatePolicies';
    /** Nome da propriedade para não validar administração de cotas (o valor é apenas salvo em Local Storage). */
    private static DONT_VALIDATE_QUOTAS_PROPERTY_NAME: string = 'dontValidateQuotas';

    private management: PoliciesQuotasManagement = {
        policies: ManagerPolicyPermissionTypes.Disabled,
        quotas: ManagerQuotaPermissionTypes.Disabled,
    };

    constructor(
        private authTokenService: AuthTokenService,
        private localStorageService: LocalStorageService,
        private managersPermissionsService: SettingsManagerPermissionsService,
        private quotasBehaviorService: SettingsQuotasActivationBehaviorService,
        private userService: UserService,
        private quotasTypeService: SettingsQuotasTypeService,
        private router: Router,
    ) { }

    /**
     *
     * @param route usar apenas durante validações de rotas (definido automaticamente pelo Angular).
     */
    public canActivate(route?: ActivatedRouteSnapshot): Observable<boolean> {
        /*
         * Caso não seja usuário gerente, retorna `true` pois a validação não se faz necessária.
         * Validações de chamadas para `canActivate` e/ou para o guard, devem ser feitas externamente (com cláusulas e/ou outros guards).
         */
        // tslint:disable-next-line:no-bitwise
        if (!this.authTokenService.isUserManager) {
            this.localStorageService.deleteValue(LocalStorageKeys.PoliciesQuotasManagement);

            return observableOf(true);
        }

        const dontValidatePolicies: boolean =
            (route && route.data[NotAllowDisabledManagementPoliciesQuotasGuardService.DONT_VALIDATE_POLICIES_PROPERTY_NAME]) || false;
        const dontValidateQuotas: boolean =
            (route && route.data[NotAllowDisabledManagementPoliciesQuotasGuardService.DONT_VALIDATE_QUOTAS_PROPERTY_NAME]) || false;

        const isAccountTypeByUser: boolean =
            this.localStorageService.getValue(LocalStorageKeys.AccountType) !== AccountTypes.PrinterDevices;

        // Verifica conforme tipo de conta (por usuário ou por impressora).
        if (isAccountTypeByUser) {
            // Verifica o tipo de cota.
            return this.quotasTypeService.getType().pipe(
                mergeMap((quotaType: QuotaObjectTypes) => {
                    // Se o tipo de cota for compartilhada, cotas não podem ser gerenciadas, buscando apenas políticas.
                    if (quotaType === QuotaObjectTypes.Shared) {
                        return this.getPoliciesPermission().pipe(
                            map((permission: boolean) => dontValidatePolicies ? true : permission));
                    }

                    // Verifica o comportamento de cota.
                    return this.quotasBehaviorService.getBehavior().pipe(
                        mergeMap((behaviorType: QuotaBehaviorTypes) => {
                            // Quando o comportamento for igual a "desabilitada", busca apenas políticas.
                            if (behaviorType === QuotaBehaviorTypes.Disabled) {
                                return this.getPoliciesPermission().pipe(
                                    map((permission: boolean) => dontValidatePolicies ? true : permission));
                            }

                            // Quando o comportamento for diferente de "desabilitada", busca políticas e cotas.
                            return observableForkJoin([this.getPoliciesPermission(), this.getQuotasPermission()]).pipe(
                                map((permissions: boolean[]) => {
                                    if (dontValidatePolicies) { return permissions[1]; }
                                    if (dontValidateQuotas) { return permissions[0]; }

                                    return permissions[0] || permissions[1];
                                }));
                        }));
                }),
                tap((allowed: boolean) => this.handlePermissions(allowed, route)));
        }

        // Para conta por impressoras, busca apenas políticas.
        return this.getPoliciesPermission().pipe(
            tap((allowed: boolean) => this.handlePermissions(allowed, route)),
            map((permission: boolean) => dontValidatePolicies ? true : permission));
    }

    private handlePermissions(allowed: boolean, route?: ActivatedRouteSnapshot): void {
        this.localStorageService.setValue(LocalStorageKeys.PoliciesQuotasManagement, this.management);

        if (!allowed && route) { this.router.navigate(['page-forbidden']); }
    }

    /** Busca administração de políticas. */
    private getPoliciesPermission(): Observable<boolean> {
        return this.managersPermissionsService.getManagerPolicyPermission().pipe(
            mergeMap((x: IManagerPolicyPermission) => {
                if (!x.allowManagement) {
                    this.management.policies = ManagerPolicyPermissionTypes.Disabled;

                    return observableOf(false);
                }

                return this.userService.getUserManagerPermissionPolicies().pipe(
                    tap((x: IUserManagerPermissionPolicies) => this.management.policies = x.permissionType),
                    map((x: IUserManagerPermissionPolicies) => x.permissionType !== ManagerPolicyPermissionTypes.Disabled));
            }));
    }

    /** Busca administração de cotas. */
    private getQuotasPermission(): Observable<boolean> {
        return this.managersPermissionsService.getManagerQuotaPermission().pipe(
            mergeMap((x: IManagerQuotaPermission) => {
                if (!x.allowManagement) {
                    this.management.quotas = ManagerQuotaPermissionTypes.Disabled;

                    return observableOf(false);
                }

                return this.userService.getUserManagerPermissionQuotas().pipe(
                    tap((x: IUserManagerPermissionQuotas) => this.management.quotas = x.permissionType),
                    map((x: IUserManagerPermissionQuotas) => x.permissionType !== ManagerQuotaPermissionTypes.Disabled));
            }));
    }
}
